import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Container, Form } from 'react-bootstrap';
import { useForm } from 'react-hook-form';
import { toast } from 'react-toastify';
import { Navbar, CustomButton, LoadingSpinner } from '../components';
import { authAPI } from '../services/api';
import './ForgotPasswordPage.css';

const ForgotPasswordPage = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [emailSent, setEmailSent] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors }
  } = useForm({
    defaultValues: {
      email: ''
    }
  });

  const onSubmit = async (data) => {
    setLoading(true);
    
    try {
      const response = await authAPI.forgotPassword({ email: data.email });
      
      if (response.status) {
        toast.success('Password reset instructions sent to your email');
        setEmailSent(true);
      } else {
        toast.error(response.message || 'Failed to send reset email. Please try again.');
      }
    } catch (error) {
      console.error('Forgot password error:', error);
      toast.error(error.message || 'Failed to send reset email. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="forgot-password-page">
      <Navbar />
      
      {loading && <LoadingSpinner fullScreen text="Sending reset instructions..." />}
      
      <Container className="forgot-password-container">
        <div className="forgot-password-header">
          <h1>Forgot Password</h1>
          <p>Enter your email address and we'll send you instructions to reset your password</p>
        </div>

        {!emailSent ? (
          <div className="forgot-password-form-wrapper">
            <Form onSubmit={handleSubmit(onSubmit)} className="forgot-password-form">
              {/* Email Field */}
              <div className="form-section">
                <label className="form-label-custom">Email Address</label>
                <Form.Control
                  type="email"
                  placeholder="you@example.com"
                  className={`form-input ${errors.email ? 'is-invalid' : ''}`}
                  {...register('email', { 
                    required: 'Email is required',
                    pattern: {
                      value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                      message: 'Invalid email address'
                    }
                  })}
                />
                {errors.email && (
                  <div className="error-message">{errors.email.message}</div>
                )}
              </div>

              <CustomButton 
                type="submit" 
                variant="primary" 
                fullWidth 
                loading={loading}
                className="forgot-password-submit"
              >
                Send Reset Instructions
              </CustomButton>

              {/* Footer */}
              <div className="forgot-password-footer">
                Remember your password? <Link to="/signin">Sign in</Link>
              </div>
            </Form>
          </div>
        ) : (
          <div className="forgot-password-success">
            <div className="success-icon">
              <svg fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
            </div>
            <h2>Check Your Email</h2>
            <p>We've sent password reset instructions to your email address. Please check your inbox and follow the instructions to reset your password.</p>
            <div className="forgot-password-actions">
              <CustomButton 
                variant="secondary" 
                onClick={() => navigate('/signin')}
              >
                Back to Sign In
              </CustomButton>
              <CustomButton 
                variant="primary" 
                onClick={() => setEmailSent(false)}
              >
                Resend Email
              </CustomButton>
            </div>
          </div>
        )}
      </Container>
    </div>
  );
};

export default ForgotPasswordPage;

